/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import { CSSProperties } from "react";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";

export const TRANSFORM_ENGINE_NODE_PADDING = 5;

// Returns all styles for the TransformEngine components.
// Dynamically reads from the current theme.
export function useTransformEngineStyles(): any {
  const { currentTheme } = useDexterTheme();

  const SELECTED_NODE_BORDER_STYLE: CSSProperties = {
    border: `3.75px solid ${currentTheme.colors.edgeSelectedColor}`,
  };

  const BASE_TRANSFORM_ENGINE_NODE_STYLE: CSSProperties = {
    background: currentTheme.colors.inputNodeBackgroundColor,
    paddingLeft: "5px",
    outline: "none",
    backgroundColor: currentTheme.colors.pageBackgroundColor,
    border: `1px solid ${currentTheme.colors.inputNodeBorderColor}`,
    borderRadius: currentTheme.dimensions.inputNodeBorderRadius,
  };

  const PARALLELOGRAM_NODE_STYLE: React.CSSProperties = {
    ...BASE_TRANSFORM_ENGINE_NODE_STYLE,
    width: "120px",
    height: "40px",
    padding: "10px",
    transform: "skew(20deg)",
  };

  const parentHandleHeight = 16;
  const parentHandleOffset = -parentHandleHeight + 20;

  const NODE_PARENT_HANDLE_STYLE: CSSProperties = {
    position: "absolute",
    right: -5.5,
    top: parentHandleOffset + 10,
    width: parentHandleHeight - parentHandleHeight / 3,
    height: parentHandleHeight,
    background: currentTheme.colors.primary,
    borderTop: `2px solid ${currentTheme.colors.inputNodeBorderColor}`,
    borderRight: `2.5px solid ${currentTheme.colors.inputNodeBorderColor}`,
    borderBottom: `2px solid ${currentTheme.colors.inputNodeBorderColor}`,
    borderLeft: "none",
    borderRadius: 0,
    zIndex: 3,
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "auto",
  };

  const OUTPUT_CIRCLE_NODE_STYLE: React.CSSProperties = {
    width: 100,
    height: 100,
    borderRadius: "50%",
    background: currentTheme.colors.inputNodeBackgroundColor,
    backgroundColor: currentTheme.colors.pageBackgroundColor,
    border: `2px solid ${currentTheme.colors.inputNodeBorderColor}`,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    opacity: 1,
    transition: "background 0.2s, opacity 0.2s",
    position: "relative",
    margin: "0 auto",
  };

  const COMPOSER_SQUARE_NODE_STYLE: React.CSSProperties = {
    width: 100,
    height: 100,
    borderRadius: "8px",
    backgroundColor: currentTheme.colors.pageBackgroundColor,
    border: `2px solid ${currentTheme.colors.inputNodeBorderColor}`,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    opacity: 1,
    transition: "background 0.2s, opacity 0.2s",
    position: "relative",
    margin: "auto",
  };

  const TRANSFORM_ENGINE_NODE_FIELD_STYLE: CSSProperties = {
    padding: `${TRANSFORM_ENGINE_NODE_PADDING}px`,
  };

  const TRANSFORM_ENGINE_NODE_HANDLE_STYLE: CSSProperties = {
    background: "#555",
  };

  const FILE_BUTTON_STYLE: CSSProperties = {
    borderRadius: "0px",
    marginLeft: "auto",
    float: "right",
    cursor: "pointer",
    color: currentTheme.colors.fileButtonColor,
    backgroundColor: currentTheme.colors.pageBackgroundColor,
  };

  const FILE_ICON_STYLE: CSSProperties = {
    backgroundColor: currentTheme.colors.secondary,
    width: "30px",
    height: "20px",
    borderRadius: "20px",
  };

  const NODE_HEADER_TEXT_STYLE: React.CSSProperties = {
    textDecoration: "none",
    fontSize: "1rem",
    fontFamily: currentTheme.fonts.secondary,
    color: currentTheme.colors.text,
  };

  const NODE_SUBTITLE_TEXT_STYLE: React.CSSProperties = {
    fontSize: "0.95rem",
    fontFamily: currentTheme.fonts.secondary,
    color: currentTheme.colors.text,
    opacity: 0.7,
    transform: "skew(-20deg)",
    marginTop: "-2px",
  };

  const EDIT_BUTTON_OVERRIDES = {
    BaseButton: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      style: ({ $isHovered }) => ({
        color: currentTheme.colors.editButtonColor,
        ":hover": {
          backgroundColor: currentTheme.colors.editButtonHoverBackgroundColor,
        },
      }),
    },
  };

  const INPUT_NODE_SEARCH_INPUT_OVERRIDES = {
    Root: {
      style: {
        borderTopWidth: "1px",
        borderRightWidth: "1px",
        borderBottomWidth: "1px",
        borderLeftWidth: "1px",
        borderTopStyle: "solid",
        borderRightStyle: "solid",
        borderBottomStyle: "solid",
        borderLeftStyle: "solid",
        borderTopColor: currentTheme.colors.primaryLight,
        borderRightColor: currentTheme.colors.primaryLight,
        borderBottomColor: currentTheme.colors.primaryLight,
        borderLeftColor: currentTheme.colors.primaryLight,
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
        backgroundColor: currentTheme.colors.dropdownBackground,
        outline: "none",
        width: "220px",
        maxWidth: "220px",
        boxSizing: "border-box",
      },
    },
    InputContainer: {
      style: {
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      },
    },
    Input: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
        color: currentTheme.colors.text,
        fontFamily: currentTheme.fonts.primary,
        fontSize: currentTheme.fontSizes.medium,
        minWidth: 0,
      },
    },
    ClearIcon: {
      style: {
        color: currentTheme.colors.text,
        ":hover": {
          color: currentTheme.colors.primaryLight,
        },
      },
    },
    ClearIconContainer: {
      style: {
        backgroundColor: currentTheme.colors.inputNodeBackgroundColor,
        color: currentTheme.colors.text,
        flexShrink: 0,
      },
    },
  } as const;

  return {
    BASE_TRANSFORM_ENGINE_NODE_STYLE,
    PARALLELOGRAM_NODE_STYLE,
    OUTPUT_CIRCLE_NODE_STYLE,
    TRANSFORM_ENGINE_NODE_PADDING,
    TRANSFORM_ENGINE_NODE_FIELD_STYLE,
    TRANSFORM_ENGINE_NODE_HANDLE_STYLE,
    FILE_BUTTON_STYLE,
    FILE_ICON_STYLE,
    NODE_HEADER_TEXT_STYLE,
    NODE_SUBTITLE_TEXT_STYLE,
    EDIT_BUTTON_OVERRIDES,
    NODE_PARENT_HANDLE_STYLE,
    COMPOSER_SQUARE_NODE_STYLE,
    INPUT_NODE_SEARCH_INPUT_OVERRIDES,
    SELECTED_NODE_BORDER_STYLE,
  };
}
